import React, { useState } from 'react';
import { formatCalendarDate } from '../../lib/calendar-date';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Header from '../../components/Header';
import DatePicker from '../../components/DatePicker';
import TimeOffDatesModal from '../../components/TimeOffDatesModal';
import TimeOffOverlaps from '../../components/TimeOffOverlaps';
import UpcomingCrewDaysOff from '../../components/UpcomingCrewDaysOff';
import { api } from '../../api/client';
import { invalidateTimeOffQueries } from '../../lib/time-off-queries';
import {
  ArrowLeft,
  CalendarDays,
  Plus,
  X,
  Loader2,
  CalendarOff,
  Pencil,
} from 'lucide-react';
import { useBack } from '../../hooks/useBack';

const TIME_OFF_TYPES = ['Vacation', 'Sick', 'Personal', 'Statutory Holiday', 'Unpaid'];

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-500/20 text-amber-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-fieldiq-dark text-fieldiq-muted',
};

const typeStyles: Record<string, string> = {
  Vacation: 'bg-blue-500/20 text-blue-400',
  Sick: 'bg-red-500/20 text-red-400',
  Personal: 'bg-purple-500/20 text-purple-400',
  'Statutory Holiday': 'bg-green-500/20 text-green-400',
  Unpaid: 'bg-fieldiq-dark text-fieldiq-muted',
};

/** Time-off bounds are calendar days. @see formatCalendarDate. */
const formatDate = (dateStr: string | null | undefined): string =>
  formatCalendarDate(dateStr, { month: 'short', day: 'numeric' }) ?? '';

export default function FieldTimeOffScreen() {
  const goBack = useBack('/field');
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [type, setType] = useState(TIME_OFF_TYPES[0]);
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState<any | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['field-time-off'],
    queryFn: () => api.employee.timeOff().then(r => r.data.data ?? r.data ?? []),
  });
  const requests: any[] = Array.isArray(data) ? data : [];

  const submitMutation = useMutation({
    mutationFn: (payload: any) => api.employee.submitTimeOff(payload),
    onSuccess: () => {
      invalidateTimeOffQueries(queryClient);
      setShowForm(false);
      setStartDate('');
      setEndDate('');
      setType(TIME_OFF_TYPES[0]);
      setReason('');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => api.employee.cancelTimeOff(id),
    onSuccess: () => {
      invalidateTimeOffQueries(queryClient);
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, startDate, endDate }: { id: string; startDate: string; endDate: string }) =>
      api.employee.editTimeOff(id, { startDate, endDate }),
    onSuccess: () => {
      invalidateTimeOffQueries(queryClient);
      setEditing(null);
    },
  });

  // Rejected and cancelled requests are terminal — nothing left to re-date.
  const canEdit = (status: string) => status === 'pending' || status === 'approved';

  const handleSubmit = () => {
    if (!startDate || !endDate) return;
    const typeMap: Record<string, string> = {
      'Vacation': 'vacation', 'Sick': 'sick', 'Personal': 'personal',
      'Statutory Holiday': 'statutory_holiday', 'Unpaid': 'unpaid',
    };
    submitMutation.mutate({ startDate, endDate, timeOffType: typeMap[type] || type.toLowerCase(), reason: reason || undefined });
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">Time Off</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {/* Request Button / Form */}
        {!showForm ? (
          <button
            onClick={() => setShowForm(true)}
            className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-xl p-3 flex items-center justify-center gap-2"
          >
            <Plus size={18} /> Request Time Off
          </button>
        ) : (
          <div className="bg-fieldiq-card rounded-xl p-4 space-y-3 border border-fieldiq-border">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-semibold text-white">New Request</h3>
              <button onClick={() => setShowForm(false)} className="text-fieldiq-muted">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">Start Date</label>
                <DatePicker value={startDate} onChange={setStartDate} placeholder="Start" />
              </div>
              <div>
                <label className="text-xs text-fieldiq-text mb-1 block">End Date</label>
                <DatePicker value={endDate} onChange={setEndDate} placeholder="End" />
              </div>
            </div>

            {/* Who else is already off, once both dates are set. Under the
                pickers on purpose: it is context for the dates just chosen, not
                a separate thing to go and look at. */}
            <TimeOffOverlaps startDate={startDate} endDate={endDate} />

            <div>
              <label className="text-xs text-fieldiq-text mb-1 block">Type</label>
              <select
                value={type}
                onChange={e => setType(e.target.value)}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white"
              >
                {TIME_OFF_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs text-fieldiq-text mb-1 block">Reason (optional)</label>
              <textarea
                value={reason}
                onChange={e => setReason(e.target.value)}
                rows={2}
                className="w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none text-white resize-none"
                placeholder="Add a note..."
              />
            </div>

            {submitMutation.isError && (
              <p className="text-red-400 text-xs">
                {(submitMutation.error as any)?.response?.data?.error || 'Failed to submit request'}
              </p>
            )}

            <button
              onClick={handleSubmit}
              disabled={!startDate || !endDate || submitMutation.isPending}
              className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold rounded-lg py-2.5 text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitMutation.isPending && <Loader2 size={16} className="animate-spin" />}
              Submit Request
            </button>
          </div>
        )}

        {/* Who on the crew is already off over the next 60 days, until the
            hand has picked BOTH dates. From then on the overlap list under the
            pickers answers for those dates, and this one would be a second,
            wider answer to the same question on the same screen. */}
        {!(showForm && startDate && endDate) && <UpcomingCrewDaysOff />}

        {/* Requests List */}
        {isLoading ? (
          <div className="text-center py-12 text-fieldiq-text">Loading requests...</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <CalendarOff size={40} className="mx-auto mb-3 text-fieldiq-muted" />
            <p className="text-fieldiq-text">No time-off requests yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((req: any) => (
              <div key={req.id} className="bg-fieldiq-card rounded-xl p-4 border border-fieldiq-border">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <CalendarDays size={16} className="text-fieldiq-accent" />
                      <span className="font-semibold text-sm text-white">
                        {formatDate(req.start_date)} – {formatDate(req.end_date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${typeStyles[req.type] || 'bg-fieldiq-dark text-fieldiq-text'}`}>
                        {req.type}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${statusStyles[req.status] || ''}`}>
                        {req.status}
                      </span>
                    </div>
                    {req.reason && (
                      <p className="text-xs text-fieldiq-muted mt-2">{req.reason}</p>
                    )}
                    {req.status === 'rejected' && req.rejection_reason && (
                      <p className="text-xs text-red-400 mt-2">Rejected: {req.rejection_reason}</p>
                    )}
                  </div>

                  {canEdit(req.status) && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => setEditing(req)}
                        className="text-xs text-fieldiq-text bg-fieldiq-dark px-3 py-1.5 rounded-lg font-medium flex items-center gap-1.5"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      {req.status === 'pending' && (
                        <button
                          onClick={() => cancelMutation.mutate(req.id)}
                          disabled={cancelMutation.isPending}
                          className="text-xs text-red-400 bg-red-500/10 px-3 py-1.5 rounded-lg font-medium"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Dates Modal */}
      {editing && (
        <TimeOffDatesModal
          startDate={editing.start_date}
          endDate={editing.end_date}
          isPending={editMutation.isPending}
          error={
            editMutation.isError
              ? (editMutation.error as any)?.response?.data?.error || 'Failed to update dates'
              : null
          }
          onCancel={() => { editMutation.reset(); setEditing(null); }}
          onSave={({ startDate, endDate }) => editMutation.mutate({ id: editing.id, startDate, endDate })}
        />
      )}
    </div>
  );
}
